// Editorial guidance, not a search-engine ranking prediction. Urdu avoids English-only reading formulas.
export function textContent(html = '') {
  const document = new DOMParser().parseFromString(html, 'text/html');
  return (document.body.textContent || '').replace(/\s+/g, ' ').trim();
}
export function makeSlug(value) {
  return String(value || '')
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .trim()
    .replace(/[\s-]+/g, '-')
    .slice(0, 140)
    .replace(/-$/, '');
}
export function analyzeSeo(post) {
  const doc = new DOMParser().parseFromString(post.content || '', 'text/html');
  const text = textContent(post.content),
    words = text.split(/\s+/u).filter(Boolean);
  const keyword = (post.seo?.focusKeyword || '').trim().toLocaleLowerCase();
  const lower = text.toLocaleLowerCase();
  const count = keyword ? lower.split(keyword).length - 1 : 0;
  const density = words.length ? ((count * keyword.split(/\s+/u).length) / words.length) * 100 : 0;
  const title = post.seo?.title || '',
    description = post.seo?.description || '';
  const headings = [...doc.querySelectorAll('h2,h3,h4')];
  const images = [...doc.images];
  const sentences = text
    .split(/[.!?۔؟]+/u)
    .map((s) => s.trim())
    .filter(Boolean);
  const avgSentence = sentences.length ? words.length / sentences.length : 0;
  const paragraphs = [...doc.querySelectorAll('p')].map(
    (p) => (p.textContent || '').split(/\s+/u).filter(Boolean).length
  );
  const checks = [];
  const add = (label, pass, advice) => checks.push({ label, pass: !!pass, advice });
  add('Focus keyword', keyword.length > 0, 'Choose the main phrase this article should answer.');
  add(
    'Keyword in SEO title',
    keyword && title.toLocaleLowerCase().includes(keyword),
    'Include the focus phrase naturally in the SEO title.'
  );
  add(
    'Keyword in introduction',
    keyword && words.slice(0, 100).join(' ').toLocaleLowerCase().includes(keyword),
    'Introduce the topic within the first 100 words.'
  );
  add(
    'Keyword in a subheading',
    keyword && headings.some((h) => h.textContent.toLocaleLowerCase().includes(keyword)),
    'Use the focus phrase in a relevant H2 or H3.'
  );
  add(
    'Keyword in description',
    keyword && description.toLocaleLowerCase().includes(keyword),
    'Mention the topic in the search description.'
  );
  add(
    'Natural keyword use',
    count >= 2 && density <= 3,
    'Use the phrase naturally; avoid repetition. Targeting a fixed density does not guarantee ranking.'
  );
  add(
    'SEO title length',
    title.length >= 30 && title.length <= 60,
    'Aim for about 30–60 characters. Search display also depends on pixel width.'
  );
  add(
    'Meta description length',
    description.length >= 100 && description.length <= 160,
    'Aim for about 100–160 characters and describe the value of reading.'
  );
  add(
    'Descriptive article length',
    words.length >= 300,
    'Cover the subject thoroughly. 300 words is guidance, not a minimum imposed by Google.'
  );
  add(
    'Clear heading structure',
    headings.length > 0 &&
      !doc.querySelector('h1') &&
      headings.every(
        (h, i) => Number(h.tagName[1]) <= (i ? Number(headings[i - 1].tagName[1]) : 1) + 1
      ),
    'Keep the page title as its only H1; use H2 then H3 without skipped levels.'
  );
  add(
    'Cover image and alt text',
    post.featuredImage?.url && post.featuredImage?.alt?.trim(),
    'Add a relevant cover image with descriptive alt text.'
  );
  add(
    'Content image alt text',
    images.every((img) => img.alt.trim()),
    'Describe every content image; do not stuff keywords.'
  );
  add(
    'Internal links',
    [...doc.links].some(
      (a) =>
        a.getAttribute('href')?.startsWith('/') ||
        a.href.startsWith('https://www.welcomehabibitrading.com/')
    ),
    'Link to a useful related article or another page on this website.'
  );
  add(
    'Short, readable URL',
    post.slug && post.slug.length <= 80,
    'Use a short, descriptive slug. Published article URLs are locked.'
  );
  const readability = [
    {
      label: 'Manageable sentences',
      pass: avgSentence > 0 && avgSentence <= (post.locale === 'ur' ? 30 : 25),
      advice: 'Break up long sentences where it improves clarity.'
    },
    {
      label: 'Short paragraphs',
      pass: paragraphs.length > 0 && paragraphs.every((n) => n <= 120),
      advice: 'Keep paragraphs focused; split blocks longer than 120 words.'
    },
    {
      label: 'Useful subheadings',
      pass: headings.length > 0 && words.length / headings.length <= 300,
      advice: 'Use descriptive subheadings to divide long sections.'
    }
  ];
  return {
    score: Math.round((checks.filter((c) => c.pass).length / checks.length) * 100),
    checks,
    readability,
    readabilityScore: Math.round(
      (readability.filter((c) => c.pass).length / readability.length) * 100
    ),
    words: words.length,
    readingMinutes: Math.max(1, Math.ceil(words.length / (post.locale === 'ur' ? 150 : 200))),
    density: density.toFixed(1),
    occurrences: count,
    localeNote:
      post.locale === 'ur'
        ? 'Urdu: language-neutral sentence, paragraph and heading checks. English reading-grade formulas are not applied.'
        : 'English: sentence length, paragraph length and heading coverage.'
  };
}
